/**
 * <tecton-why-list> — scroll-tracked argument list.
 *
 * Marks items active as they pass the reading line and fills the spine to the
 * furthest item reached. The fill only ever advances: scrolling back up keeps
 * the argument you have already read lit, because un-filling it would suggest
 * you had somehow un-read it.
 *
 * Under prefers-reduced-motion every item is marked active immediately — the
 * dimming exists to track scroll position, and without scroll animation it
 * would just be low-contrast text for no reason.
 */

class TectonWhyList extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-why-item]')];
    this.spine = this.querySelector('[data-spine]');
    if (this.items.length === 0) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      for (const item of this.items) item.setAttribute('data-active', '');
      if (this.spine) this.spine.style.height = '100%';
      return;
    }

    this.furthest = -1;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = this.items.indexOf(entry.target);
          if (index > this.furthest) this.furthest = index;
        }
        // Light everything up to the furthest point rather than only the items
        // that happened to fire — a fast scroll skips intersections and would
        // otherwise leave read items dimmed behind the current one.
        this.items.forEach((item, i) => {
          item.toggleAttribute('data-active', i <= this.furthest);
        });
        this.fill();
      },
      // Trigger when an item reaches the middle of the viewport, which is
      // roughly where a reader's attention sits.
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    for (const item of this.items) this.observer.observe(item);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  fill() {
    if (!this.spine || this.furthest < 0) return;
    const pct = ((this.furthest + 1) / this.items.length) * 100;
    this.spine.style.height = `${pct}%`;
  }
}

if (!customElements.get('tecton-why-list')) {
  customElements.define('tecton-why-list', TectonWhyList);
}
